import { apiClient } from '~/api/client'
import type {
    ChunkAddResult,
    ChunkDeleteResult,
    ChunkEditResult,
    DocumentBulkResult,
    DocumentChunksResponse,
    DocumentUploadResult,
    KnowledgeCorpus,
} from '~/api/types'
import type { DocumentRow } from '~/components/knowledge/UploadManagementSection'

// 지식베이스 문서 API. 단건 재색인/삭제는 페이지가 기존 엔드포인트로 직접 호출하고,
// 여기서는 선택 항목 일괄 처리만 다룬다(카탈로그 bulk 패턴과 동일 톤).

/** GET /admin/api/content/knowledge/corpus — 벡터 스토어(Qdrant)에 실제 색인된 RAG 코퍼스.
 *  Postgres documents(업로드 파이프라인)와 달리, 운영 CLI로 적재한 청크 코퍼스를 읽는다.
 *  available=false면 벡터 스토어 연결 불가 상태로 안내만 표시한다. */
export function getKnowledgeCorpus() {
    return apiClient.get<KnowledgeCorpus>('/content/knowledge/corpus')
}

/** GET /admin/api/content/knowledge/corpus/documents/{documentId}/chunks — 코퍼스 문서 한 건의
 *  청크를 본문 검색(q)·페이지네이션(limit/offset, 0-base)으로 조회한다(read-only 드릴다운). */
export function getDocumentChunks(
    documentId: string,
    params: { q?: string; limit: number; offset: number }
) {
    const search = new URLSearchParams()
    const q = params.q?.trim()
    if (q) search.set('q', q)
    search.set('limit', String(params.limit))
    search.set('offset', String(params.offset))
    return apiClient.get<DocumentChunksResponse>(
        `/content/knowledge/corpus/documents/${encodeURIComponent(
            documentId
        )}/chunks?${search.toString()}`
    )
}

/** DELETE /admin/api/content/knowledge/corpus/documents/{documentId}/chunks/{chunkId} —
 *  코퍼스에서 청크 한 건을 삭제한다(superadmin). Qdrant 포인트를 지워 검색·상담에서 즉시
 *  제외하고 공유 chunks 행도 정리하는 큐레이션 삭제다. 재색인하면 원본 기준으로 다시 채워진다. */
export function deleteChunk(documentId: string, chunkId: string) {
    return apiClient.delete<ChunkDeleteResult>(
        `/content/knowledge/corpus/documents/${encodeURIComponent(
            documentId
        )}/chunks/${encodeURIComponent(chunkId)}`
    )
}

/** PUT /admin/api/content/knowledge/corpus/documents/{documentId}/chunks/{chunkId} —
 *  코퍼스 청크 한 건의 본문을 편집한다(superadmin). 새 본문을 embedding.corpus 모델로
 *  재임베딩해 같은 chunkId 포인트에 업서트하므로 검색·상담에 즉시 반영되고 색인·질의와 같은
 *  벡터 공간을 유지한다. 재색인하면 원본 기준으로 덮어써지므로, 편집 유지엔 수동 관리 잠금이 필요하다. */
export function editChunk(documentId: string, chunkId: string, text: string) {
    return apiClient.put<ChunkEditResult>(
        `/content/knowledge/corpus/documents/${encodeURIComponent(
            documentId
        )}/chunks/${encodeURIComponent(chunkId)}`,
        { text }
    )
}

/** POST /admin/api/content/knowledge/corpus/documents/{documentId}/chunks —
 *  코퍼스 문서에 청크 한 건을 새로 추가한다(superadmin). 기존 청크 하나를 payload 템플릿으로
 *  삼아 코퍼스 shape을 상속하되 새 본문(text)을 embedding.corpus 모델로 임베딩해 새 포인트로
 *  업서트하므로 검색·상담에 즉시 반영된다. order는 기존 최대 order+1이다. 재색인하면 원본
 *  기준으로 덮어써지므로, 추가 유지엔 수동 관리 잠금이 필요하다. */
export function addChunk(documentId: string, text: string) {
    return apiClient.post<ChunkAddResult>(
        `/content/knowledge/corpus/documents/${encodeURIComponent(
            documentId
        )}/chunks`,
        { text }
    )
}

/** POST /admin/api/documents/{id}/curation-lock — 수동 관리 잠금을 켜거나 끈다(admin).
 *  잠금 ON이면 재색인이 이 문서를 건너뛰어(서버가 request_reindex 거부) 수동 편집·삭제가
 *  원본 재처리로 덮어써지지 않는다. 갱신된 문서(curationLocked 포함)를 돌려준다. */
export function setCurationLock(documentId: string, locked: boolean) {
    return apiClient.post<DocumentRow>(
        `/documents/${encodeURIComponent(documentId)}/curation-lock`,
        { locked }
    )
}

/** POST /admin/api/documents — 원본 파일(PDF·Markdown·Excel)을 멀티파트로 업로드해 문서로
 *  등록한다(→ pending). 서버가 오브젝트 스토리지에 원본을 저장하고, 워커가 내려받아 인덱싱한다.
 *  등록 직후엔 청크가 아직 적재 전이라 ‘색인된 코퍼스’에 바로 보이지 않을 수 있다. */
export function uploadDocument(
    file: File,
    opts: { title: string; domain: string }
) {
    const form = new FormData()
    form.append('file', file)
    form.append('title', opts.title)
    form.append('domain', opts.domain)
    return apiClient.postForm<DocumentUploadResult>('/documents', form)
}

/** GET /admin/api/documents/{id}/download — 원본 파일을 어드민 백엔드가 오브젝트 스토리지에서
 *  프록시 스트리밍으로 내려준다. MinIO 엔드포인트가 클러스터 내부라 presigned URL 대신 백엔드가
 *  바이트를 프록시한다. 세션 쿠키가 실려야 하고, 404(원본 없음)를 토스트로 처리하려 window.open이
 *  아니라 blob fetch로 받아 임시 <a download>로 저장한다(catalog export와 동일 다운로드 경로).
 *  파일명은 Content-Disposition(RFC 5987 filename*)에서 뽑고, 없으면 fallbackName을 쓴다. */
export function downloadDocument(id: string, fallbackName: string) {
    return apiClient.downloadBlob(
        `/documents/${encodeURIComponent(id)}/download`,
        fallbackName
    )
}

/** 문서 일괄 작업 종류. reindex=재색인, delete=삭제(색인 청크 포함). */
export type DocumentBulkAction = 'reindex' | 'delete'

/** POST /admin/api/documents/bulk — 선택한 문서들을 한 번에 재색인하거나 삭제(admin).
 *  부분 실패는 {id,error}로 모여 토스트에 실패 수로 표시된다. */
export function bulkDocuments(ids: string[], action: DocumentBulkAction) {
    return apiClient.post<DocumentBulkResult>('/documents/bulk', {
        ids,
        action,
    })
}
